import $ from 'jquery';
import React, {
    Fragment, useContext, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { renderWithRoot, unmountRootAtNode } from '../utilities/reactRoot';
import { canDeleteVerificationSchedule } from '../utilities/permissionsUtilities';
import useUsers from '../hooks/useUsers';
import {
    getApiCreateLinkVerificationSchedule,
    getApiGetLinkVerificationAssigneesUrl,
    getApiLinkVerificationSchedule,
} from '../utilities/urlHelper';
import { addSuccessMessage } from '../utilities/notificationUtilities';
import {
    LoadingViewVerificationScheduleModal,
    SelectVerificationAssignees,
    VerificationCreator,
} from './page/VerificationScheduleWrapper';
import { VerificationListContext } from './analytics/VerificationList';
import { getIndexResourceId } from '../utilities/schemaUtility';
import { pluralText } from '../utilities/wordUtilities';

const ViewLinkVerificationModal = (props) => {
    const [verificationSchedule, setVerificationSchedule] = useState();
    const [isDeleting, setIsDeleting] = useState(false);

    const {
        id, title, closeModal, verification_schedule_id,
    } = props;
    const resourceId = getIndexResourceId(id);

    useEffect(() => {
        const url = getApiLinkVerificationSchedule(resourceId, verification_schedule_id);
        $.get(url)
            .then(({ verification_schedule }) => setVerificationSchedule(verification_schedule));
    }, [verification_schedule_id]);

    const onClickModal = ({ target }) => {
        const className = target.getAttribute('class');
        if (className && className.includes('react-modal open')) {
            return props.closeModal();
        }
    };

    const deleteSchedule = () => {
        const url = getApiLinkVerificationSchedule(resourceId, verification_schedule_id);
        const type = 'DELETE';
        if (isDeleting) return;
        setIsDeleting(true);
        $.ajax({ url, type })
            .then(() => {
                setIsDeleting(false);
                props.onDelete(null);
                addSuccessMessage('Verification deleted');
                return props.closeModal();
            });
    };

    if (!verificationSchedule) {
        return (
            <LoadingViewVerificationScheduleModal title={props.title} closeModal={props.closeModal} />);
    }

    const {
        user_id, days, description, last_verified_at, created_at,
    } = verificationSchedule;
    const deleteButtonText = isDeleting ? 'Deleting...' : 'Delete verification';
    const deleteButtonClass = isDeleting ? 'is-disabled' : '';

    const LastVerifiedText = !last_verified_at ? 'Not verified yet' : moment.utc(last_verified_at).local().fromNow();
    const DueText = !last_verified_at ? 'Now' : moment.utc(last_verified_at).local().startOf('day').add(days, 'days')
        .format('ll');

    return (
        <div className="react-modal open" onMouseDown={onClickModal}>
            <div className="modal-content p2">
                <h2>
                    "
                    {title}
" verification
                </h2>
                <div className="form-group mb3">
                    <VerificationAssignee link_id={resourceId} />
                    {description && (
                        <div className="mb2">
                            <span className="bold block col-12 mt2">Instructions</span>
                            <span className="col-12 mb2">{description}</span>
                        </div>
                    )}
                    <div className="flex">
                        <div className="flex-auto">
                            <span className="bold block col-12">Verification period</span>
                            <span className="col-12">
                                {`Every ${pluralText(days, 'day')}`}
                            </span>
                        </div>
                        <div className="flex-auto">
                            <span className="bold block col-12">Last verified</span>
                            <span className="col-12">{LastVerifiedText}</span>
                        </div>
                    </div>
                    <span className="bold block col-12 mt2">Due</span>
                    <span className="col-12">{DueText}</span>
                    <VerificationCreator user_id={user_id} created_at={created_at} />
                </div>
                <div className="right-align mt2">
                    {(canDeleteVerificationSchedule() && props.onDelete)
                    && (
                        <span
                            className={`btn btn-delete mr1 ${deleteButtonClass}`}
                            aria-label="Delete"
                            role="button"
                            onClick={deleteSchedule}
                        >
                            {deleteButtonText}
                        </span>
                    )}
                    <span
                        className="btn-secondary"
                        aria-label="Close"
                        role="button"
                        onClick={closeModal}
                    >
                        Close
                    </span>
                </div>
            </div>
        </div>
    );
};
const VerificationAssignee = ({ link_id }) => {
    let assignees;
    const [assigneesArr, setAssigneesArr] = useState();
    const users = useUsers();
    const url = getApiGetLinkVerificationAssigneesUrl(link_id);

    useEffect(() => {
        $.get(url)
            .then(({ assignees }) => {
                setAssigneesArr(assignees);
            });
    }, [url]);
    if (assigneesArr) {
        assignees = users && users.filter(u => (assigneesArr.includes(u.id)));
    }
    return (
        <Fragment>
            <label className="bold block col-12 mb1">Assignee(s)</label>
            {!assignees
                ? (<span className="loading grey">Loading</span>)
                : assignees.map((element, index, array) => (
                    <div key={index} className="block col-12 mb2">
                        <div className="flex">
                            <img
                                className="post-avatar mr1 flex-none"
                                src={element.image_192}
                                width="32"
                                height="32"
                            />
                            <span className="flex-auto elipsis">
                                {element.display_name}
                                {' '}
                            </span>
                        </div>
                    </div>
                ))}
        </Fragment>
    );
};

const CreateOrEditLinkVerificationModal = (props) => {
    const verificationList = useContext(VerificationListContext);
    const {
        id, title, closeModal, verification_schedule, verification_schedule_id,
    } = props;
    const descriptionInput = useRef(null);
    const daysInput = useRef(null);
    const [isCreating, setIsCreating] = useState(false);
    const [assignees, setAssignees] = useState(false);
    const users = useUsers();

    const editOrCreateText = verification_schedule ? 'Update' : 'Add';
    const defaultDays = verification_schedule ? verification_schedule.days : 90;
    const defaultDescription = verification_schedule ? verification_schedule.description : '';
    const assigneeIds = verification_schedule ? verification_schedule.link_verification_assignee.map(a => a.assignee_id) : [];
    const defaultAssignees = assigneeIds.length ? (users && users.filter(u => (assigneeIds.includes(u.id))).map(u => ({ label: u.display_name, value: u.id }))) : [];

    const onClickModal = ({ target }) => {
        const className = target.getAttribute('class');
        if (className && className.includes('react-modal open')) {
            return props.closeModal();
        }
    };
    const handleAssigneeSelect = (list) => {
        setAssignees(list);
    };
    const onClickCreate = () => {
        const description = descriptionInput.current.value;
        const days = daysInput.current.value;
        if (isCreating || !days || parseInt(days) < 1) return;
        setIsCreating(true);

        const resourceId = getIndexResourceId(id);

        const url = verification_schedule ? getApiLinkVerificationSchedule(resourceId, verification_schedule_id) : getApiCreateLinkVerificationSchedule(resourceId);
        const type = verification_schedule ? 'PUT' : 'POST';
        $.post({
            url,
            data: { days, description, assignees },
            type,
        }).then(({ verification_schedule }) => {
            setIsCreating(false);
            addSuccessMessage(`Verification ${verification_schedule_id ? 'updated!' : 'created!'}`);
            if (verificationList) {
                const verifications = verificationList.verifications.map((v) => {
                    if (v.id === resourceId && v.verification_schedule_id === verification_schedule_id) {
                        v.verification_schedule.link_verification_assignee = verification_schedule.link_verification_assignee;
                        v.verification_schedule.days = verification_schedule.days;
                        v.verification_schedule.description = verification_schedule.description;
                    }
                    return v;
                });
                verificationList.setVerifications(verifications);
            }
            return props.setVerificationScheduleIdAndClose(verification_schedule.id);
        });
    };

    const createButtonText = isCreating ? 'Loading...' : `${editOrCreateText} verification`;
    const createButtonClass = isCreating ? 'is-disabled' : '';

    return (
        <div className="react-modal open" onMouseDown={onClickModal}>
            <div className="modal-content p2">
                <h2>
                    {editOrCreateText}
                    {' '}
verification to "
                    {title}
"
                </h2>
                <div className="form-group mb3">

                    <SelectVerificationAssignees verificationScheduleId={verification_schedule_id} assignees={defaultAssignees} onAssigneeSelect={handleAssigneeSelect} />

                    <label className="bold block col-12 mb1">How often should this link be verified? (days)</label>
                    <input
                        className="block col-3 mb2 field"
                        ref={daysInput}
                        defaultValue={defaultDays}
                        min="1"
                        max="365"
                        type="number"
                    />
                    <label className="bold block col-12 mb1">Add instructions to this verification (optional)</label>
                    <input
                        className="block col-12 mb2 field"
                        ref={descriptionInput}
                        placeholder="e.g Check link is up-to-date..."
                        defaultValue={defaultDescription}
                        type="text"
                    />
                </div>
                <div className="right-align mt2">
                    <span
                        className="btn-secondary mr1"
                        aria-label="Close"
                        role="button"
                        onClick={closeModal}
                    >
Cancel
                    </span>
                    <span
                        className={`btn btn-primary ${createButtonClass}`}
                        onClick={onClickCreate}
                    >
                        {createButtonText}
                    </span>
                </div>
            </div>
        </div>
    );
};

const CreateOrEditLinkVerificationWrapper = (props) => {
    const editOrCreateText = props.verification_schedule ? 'Edit' : 'Add';
    return (
        <Fragment>
            <span className="dropdown-option blue" onClick={() => props.setShowModal(true)}>
                {editOrCreateText}
                {' '}
verification
            </span>
            {props.showModal && createPortal(<CreateOrEditLinkVerificationModal
                {...props}
                closeModal={() => props.setShowModal(false)}
            />, document.body)}
        </Fragment>
    );
};

const ViewLinkVerificationWrapper = (props) => (
    <Fragment>
        <span className="dropdown-option blue view-verification" onClick={() => props.setShowModal(true)}>View verification</span>
        {props.showModal && createPortal(
            <ViewLinkVerificationModal {...props} closeModal={() => props.setShowModal(false)} />,
            document.body,
        )}
    </Fragment>
);

const LinkVerificationWrapper = (props) => {
    const [showModal, setShowModal] = useState(false);
    const { verification_schedule_id } = props;
    const deleteHandler = (id) => {
        props.onSubmit(id);
    };
    const afterSubmission = id => {
        props.onSubmit(id);
        setShowModal(false);
    };

    return (
        verification_schedule_id
            ? (
                <ViewLinkVerificationWrapper
                    showModal={showModal}
                    setShowModal={setShowModal}
                    {...props}
                    onDelete={deleteHandler}
                />
            )
            : (
                <CreateOrEditLinkVerificationWrapper
                    showModal={showModal}
                    setShowModal={setShowModal}
                    {...props}
                    setVerificationScheduleIdAndClose={afterSubmission}
                />
            )
    );
};

const closeAddExternalResourceModal = () => {
    unmountRootAtNode($('#react-link-modal')[0]);
    $('#react-link-modal').empty();
};

$('body').on('click', '[data-trigger-verification-external-resource]', (e) => {
    const { id, title, verification_schedule_id } = e.target.dataset;
    const afterSubmission = () => {
        closeAddExternalResourceModal();
        setTimeout(() => {
            window.location.reload();
        }, 1500);
    };

    renderWithRoot(
        verification_schedule_id
            ? (
                <ViewLinkVerificationModal
                    closeModal={closeAddExternalResourceModal}
                    title={title}
                    verification_schedule_id={verification_schedule_id}
                    id={id}
                    onDelete={afterSubmission}
                    setVerificationScheduleIdAndClose={afterSubmission}
                />
            )
            : (
                <CreateOrEditLinkVerificationModal
                    closeModal={closeAddExternalResourceModal}
                    title={title}
                    id={id}
                    setVerificationScheduleIdAndClose={afterSubmission}
                />
            ),
        $('#react-link-modal')[0],
    );
});

$('body').on('mousedown', '.react-modal.verification-external-resource-modal', (e) => {
    const $target = $(e.target);

    if ($target.hasClass('open')) {
        closeAddExternalResourceModal();
    }
});

export { CreateOrEditLinkVerificationWrapper };

export { ViewLinkVerificationWrapper };

export default LinkVerificationWrapper;
